import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { endOfMonth, format, isToday, isYesterday, startOfMonth, subMonths } from "date-fns";
import { Filter, Plus, Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ImportDialog from "@/components/TransactionImport/ImportDialog";
import BulkActionBar from "@/components/transactions/BulkActionBar";
import DayGroup from "@/components/transactions/DayGroup";
import EditTransactionDialog from "@/components/transactions/EditTransactionDialog";
import FilterBar, { type PeriodKey } from "@/components/transactions/FilterBar";
import FilterChips from "@/components/transactions/FilterChips";
import ViewToggle from "@/components/transactions/ViewToggle";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useExpense, type Transaction } from "@/contexts/ExpenseContext";
import { transactionApi } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

type ViewMode = "grouped" | "list";
type SourceFilter = "whatsapp" | "cc-sync" | "recurring" | "web" | "automation";

const TOKEN_STYLE = {
  "--bg-primary": "#181B34",
  "--bg-secondary": "#1F2244",
  "--bg-hover": "#292D4A",
  "--bg-deep": "#0E1128",
  "--border-color": "#3C4057",
  "--border-subtle": "rgba(255,255,255,0.08)",
  "--text-primary": "#FFFFFF",
  "--text-secondary": "#A9AEBE",
  "--text-muted": "#6E7390",
  "--fg-accent": "#6161FF",
  "--fg-accent-hover": "#7575FF",
  "--fg-accent-soft": "rgba(97,97,255,0.12)",
  "--fg-accent-ring": "rgba(97,97,255,0.18)",
  "--income": "#00CA72",
  "--expense": "#FB275D",
} as CSSProperties;

function normalizeSource(transaction: Transaction): SourceFilter {
  const source = transaction.source || "web";
  if (transaction.isPeriodicPayment && (source === "web" || !source)) return "recurring";
  if (source === "credit-card-sync") return "cc-sync";
  if (source === "import") return "web";
  if (source === "whatsapp") return "whatsapp";
  if (source === "recurring") return "recurring";
  if (source === "automation") return "automation";
  return "web";
}

function parseInstallment(description: string): { current: number; total: number; clean: string } | null {
  const match = description.match(/\s*\[(\d+)\/(\d+)\]\s*$/);
  if (!match) return null;
  return {
    current: parseInt(match[1], 10),
    total: parseInt(match[2], 10),
    clean: description.replace(/\s*\[(\d+)\/(\d+)\]\s*$/, "").trim(),
  };
}

export default function Transactions() {
  const { transactions, categories, loadTransactions, loading, deleteTransaction, updateTransaction } = useExpense();
  const { currency } = useCurrency();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const isHe = i18n.language === "he";
  const locale = isHe ? "he-IL" : "en-US";

  const [view, setView] = useState<ViewMode>("grouped");
  const [period, setPeriod] = useState<PeriodKey>("this");
  const [search, setSearch] = useState("");
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [activeSource, setActiveSource] = useState<SourceFilter | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [customFrom, setCustomFrom] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [customTo, setCustomTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState<string | null>(null);
  const [deleteTransactionId, setDeleteTransactionId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const txText = isHe
    ? {
        title: "תנועות",
        transactions: "תנועות",
        byDay: "לפי יום",
        list: "רשימה",
        import: "ייבוא",
        add: "הוסף תנועה",
        search: "חיפוש תנועות...",
        filters: "מסננים",
        allCategories: "כל הקטגוריות",
        seeAll: "הצג הכל",
        thisMonth: "החודש",
        lastMonth: "חודש שעבר",
        last3Months: "3 חודשים אחרונים",
        customRange: "טווח מותאם",
        today: "היום",
        yesterday: "אתמול",
        items: "תנועות",
        selected: "נבחרו",
        changeCategory: "שנה קטגוריה",
        delete: "מחק",
        closeSelection: "נקה בחירה",
        selectCategory: "בחר קטגוריה",
        categoryDialogTitle: "שנה קטגוריה",
        apply: "החל",
        cancel: "ביטול",
        filter: "סינון",
        mobileFiltersTitle: "מסננים",
        mobileSearchLabel: "חיפוש",
        categoriesTitle: "קטגוריות",
        sourcesTitle: "מקורות",
        noTransactions: "לא נמצאו תנועות",
        source: {
          whatsapp: "וואטסאפ",
          "cc-sync": "סנכרון אשראי",
          recurring: "הוראת קבע",
          web: "ידני",
          automation: "אוטומציה",
        } as Record<SourceFilter, string>,
      }
    : {
        title: "Transactions",
        transactions: "transactions",
        byDay: "By day",
        list: "List",
        import: "Import",
        add: "Add transaction",
        search: "Search transactions...",
        filters: "Filters",
        allCategories: "All categories",
        seeAll: "See all",
        thisMonth: "This month",
        lastMonth: "Last month",
        last3Months: "Last 3 months",
        customRange: "Custom range",
        today: "Today",
        yesterday: "Yesterday",
        items: "items",
        selected: "selected",
        changeCategory: "Change category",
        delete: "Delete",
        closeSelection: "Clear selection",
        selectCategory: "Select category",
        categoryDialogTitle: "Change category",
        apply: "Apply",
        cancel: "Cancel",
        filter: "Filter",
        mobileFiltersTitle: "Filters",
        mobileSearchLabel: "Search",
        categoriesTitle: "Categories",
        sourcesTitle: "Sources",
        noTransactions: "No transactions found",
        source: {
          whatsapp: "WhatsApp",
          "cc-sync": "Card sync",
          recurring: "Recurring",
          web: "Manual",
          automation: "Automation",
        } as Record<SourceFilter, string>,
      };

  const periodOptions = [
    { value: "this" as const, label: txText.thisMonth },
    { value: "last" as const, label: txText.lastMonth },
    { value: "last3" as const, label: txText.last3Months },
    { value: "custom" as const, label: txText.customRange },
  ];

  const dateRange = useMemo(() => {
    const now = new Date();
    if (period === "last") {
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    if (period === "last3") {
      return { from: startOfMonth(subMonths(now, 3)), to: endOfMonth(now) };
    }
    if (period === "custom") {
      return { from: new Date(customFrom), to: new Date(customTo) };
    }
    return { from: startOfMonth(now), to: endOfMonth(now) };
  }, [period, customFrom, customTo]);

  useEffect(() => {
    void loadTransactions({
      from: format(dateRange.from, "yyyy-MM-dd"),
      to: format(dateRange.to, "yyyy-MM-dd"),
    });
  }, [dateRange.from, dateRange.to, loadTransactions]);

  const visibleCategories = useMemo(
    () =>
      categories.map((category) => ({
        id: category.id,
        label: category.name,
        color: category.color,
      })),
    [categories],
  );

  const filteredTransactions = useMemo(() => {
    return transactions
      .filter((transaction) => {
        const q = search.trim().toLowerCase();
        const normalizedSource = normalizeSource(transaction);
        const categoryId =
          transaction.categoryId || categories.find((category) => category.name === transaction.category)?.id;
        const txDate = new Date(transaction.date);

        if (q) {
          const inDesc = transaction.description.toLowerCase().includes(q);
          if (!inDesc) return false;
        }
        if (activeCategoryId && categoryId !== activeCategoryId) return false;
        if (activeSource && normalizedSource !== activeSource) return false;
        if (period === "custom" && (txDate < dateRange.from || txDate > dateRange.to)) return false;
        return true;
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [transactions, search, activeCategoryId, activeSource, period, dateRange.from, dateRange.to, categories]);

  const totals = useMemo(() => {
    const normalizedAmounts = filteredTransactions
      .map((transaction) => Number(transaction.amount))
      .filter((amount) => Number.isFinite(amount));

    const expense = normalizedAmounts
      .filter((amount) => amount < 0)
      .reduce((sum, amount) => sum + Math.abs(amount), 0);
    const income = normalizedAmounts
      .filter((amount) => amount > 0)
      .reduce((sum, amount) => sum + amount, 0);

    return {
      expense: Number.isFinite(expense) ? expense : 0,
      income: Number.isFinite(income) ? income : 0,
    };
  }, [filteredTransactions]);

  const groups = useMemo(() => {
    const grouped = filteredTransactions.reduce<Record<string, Transaction[]>>((acc, transaction) => {
      (acc[transaction.date] ??= []).push(transaction);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, txs]) => {
        const parsedDate = new Date(date);
        const label = isToday(parsedDate)
          ? txText.today
          : isYesterday(parsedDate)
            ? txText.yesterday
            : parsedDate.toLocaleDateString(locale, { weekday: "short", month: "short", day: "numeric" });
        return { key: date, label, txs };
      });
  }, [filteredTransactions, txText.today, txText.yesterday, locale]);

  const mappedRows = (items: Transaction[], mode: ViewMode) =>
    items.map((transaction) => {
      const installment = parseInstallment(transaction.description);
      const category =
        categories.find((cat) => cat.id === transaction.categoryId) ||
        categories.find((cat) => cat.name === transaction.category);
      const source = normalizeSource(transaction);
      return {
        id: transaction.id,
        description:
          transaction.businessName?.trim() ||
          installment?.clean ||
          transaction.description,
        amount: transaction.amount,
        categoryName: transaction.category,
        categoryColor: category?.color || transaction.categoryInfo?.color || "#579BFC",
        paymentLabel: transaction.paymentType || "Card",
        dateLabel: mode === "list" ? new Date(transaction.date).toLocaleDateString(locale) : undefined,
        source,
        sourceLabel: txText.source[source],
        installment: installment ? { current: installment.current, total: installment.total } : undefined,
        canEdit: true,
      };
    });

  const editingTransaction = editingTransactionId
    ? transactions.find((transaction) => transaction.id === editingTransactionId) ?? null
    : null;

  const handleEdit = (id: string) => {
    const transaction = transactions.find((tx) => tx.id === id);
    if (!transaction) return;
    setEditingTransactionId(id);
  };

  const handleSaveEdit = async (id: string, updates: Partial<Transaction>) => {
    await updateTransaction(id, updates);
    await loadTransactions({
      from: format(dateRange.from, "yyyy-MM-dd"),
      to: format(dateRange.to, "yyyy-MM-dd"),
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTransactionId) return;
    setIsDeleting(true);
    try {
      await deleteTransaction(deleteTransactionId);
      setDeleteTransactionId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const rowActionLabels = {
    editTooltip: t("transactions.editTooltip"),
    editDisabledTooltip: t("transactions.editDisabledTooltip"),
    deleteTooltip: t("transactions.deleteTooltip"),
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyBulkCategory = async () => {
    if (!bulkCategoryId || selectedIds.size === 0) return;
    await transactionApi.bulkUpdateCategory(Array.from(selectedIds), bulkCategoryId);
    toast.success(isHe ? "הקטגוריה עודכנה" : "Category updated");
    setSelectedIds(new Set());
    setBulkCategoryId("");
    await loadTransactions({
      from: format(dateRange.from, "yyyy-MM-dd"),
      to: format(dateRange.to, "yyyy-MM-dd"),
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    await transactionApi.bulkDelete(Array.from(selectedIds));
    toast.success(isHe ? "התנועות נמחקו" : "Transactions deleted");
    setSelectedIds(new Set());
    await loadTransactions({
      from: format(dateRange.from, "yyyy-MM-dd"),
      to: format(dateRange.to, "yyyy-MM-dd"),
    });
  };

  return (
    <div className="min-h-full bg-[var(--bg-primary)] pb-8 pt-2" dir={isHe ? "rtl" : "ltr"} style={TOKEN_STYLE}>
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-4 px-4 md:px-8">
        <header className="mb-1 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-[26px] font-semibold tracking-[-0.015em] text-[var(--text-primary)]">{txText.title}</h2>
            <div
              className="mt-1 text-[14px] font-normal text-[var(--text-muted)]"
              style={{ fontVariantNumeric: "tabular-nums", fontFeatureSettings: '"tnum"', letterSpacing: "-0.01em" }}
            >
              {(() => {
                const safeExpense = Number.isFinite(totals.expense) ? totals.expense : 0;
                const safeIncome = Number.isFinite(totals.income) ? totals.income : 0;
                return (
                  <>
                    {filteredTransactions.length} {txText.transactions} ·{" "}
                    <span className="text-[var(--expense)]">−{currency.symbol}{Math.round(safeExpense).toLocaleString(locale)}</span>{" "}
                    ·{" "}
                    <span className="text-[var(--income)]">+{currency.symbol}{Math.round(safeIncome).toLocaleString(locale)}</span>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="hidden flex-wrap items-center gap-2 md:flex">
            <ViewToggle view={view} onChange={setView} byDayLabel={txText.byDay} listLabel={txText.list} />
            <Button
              variant="outline"
              className="h-[36px] rounded-[8px] border-[var(--border-color)] bg-transparent px-[14px] py-[9px] text-[13px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
              onClick={() => setImportOpen(true)}
            >
              <Upload size={14} className="me-2" />
              {txText.import}
            </Button>
            <Button
              className="h-[36px] rounded-[8px] bg-[var(--fg-accent)] px-[14px] py-[9px] text-[13px] font-medium text-white hover:bg-[var(--fg-accent-hover)]"
              onClick={() => navigate("/add-expense")}
            >
              <Plus size={14} className="me-2" />
              {txText.add}
            </Button>
          </div>

          <div className="flex w-full items-center justify-between gap-2 md:hidden">
            <ViewToggle view={view} onChange={setView} byDayLabel={txText.byDay} listLabel={txText.list} />
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-[8px] border-[var(--border-color)] bg-transparent text-[var(--text-primary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
                onClick={() => setImportOpen(true)}
                aria-label={txText.import}
              >
                <Upload size={14} />
              </Button>
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 rounded-[8px] border-[var(--border-color)] bg-transparent text-[var(--text-primary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
                    aria-label={txText.filter}
                  >
                    <Filter size={14} />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side={isHe ? "left" : "right"}
                  className="w-[92vw] max-w-sm border-[var(--border-color)] bg-[var(--bg-secondary)] px-4"
                >
                  <SheetHeader>
                    <SheetTitle className="text-[16px] font-semibold text-[var(--text-primary)]">
                      {txText.mobileFiltersTitle}
                    </SheetTitle>
                  </SheetHeader>

                  <div className="mt-4 space-y-4">
                    <div className="space-y-2">
                      <label className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        {txText.mobileSearchLabel}
                      </label>
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-10 w-full rounded-[8px] border border-[var(--border-color)] bg-[var(--bg-deep)] px-3 text-[13px] text-[var(--text-primary)] outline-none"
                        placeholder={txText.search}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        {txText.customRange}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {periodOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`rounded-[8px] border px-3 py-2 text-[12px] ${
                              period === option.value
                                ? "border-[var(--fg-accent)] bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]"
                                : "border-[var(--border-color)] text-[var(--text-secondary)]"
                            }`}
                            onClick={() => setPeriod(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        {txText.categoriesTitle}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          className={`rounded-[100px] border px-3 py-1.5 text-[12px] ${
                            !activeCategoryId
                              ? "border-[var(--fg-accent)] bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]"
                              : "border-[var(--border-color)] text-[var(--text-secondary)]"
                          }`}
                          onClick={() => setActiveCategoryId(null)}
                        >
                          {txText.allCategories}
                        </button>
                        {visibleCategories.map((category) => (
                          <button
                            key={category.id}
                            type="button"
                            className={`inline-flex items-center gap-1.5 rounded-[100px] border px-3 py-1.5 text-[12px] ${
                              activeCategoryId === category.id
                                ? "border-[var(--fg-accent)] bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]"
                                : "border-[var(--border-color)] text-[var(--text-secondary)]"
                            }`}
                            onClick={() =>
                              setActiveCategoryId(activeCategoryId === category.id ? null : category.id)
                            }
                          >
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color }} />
                            {category.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        {txText.sourcesTitle}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {(Object.keys(txText.source) as SourceFilter[]).map((source) => (
                          <button
                            key={source}
                            type="button"
                            className={`rounded-[100px] border px-3 py-1.5 text-[12px] ${
                              activeSource === source
                                ? "border-[var(--fg-accent)] bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]"
                                : "border-[var(--border-color)] text-[var(--text-secondary)]"
                            }`}
                            onClick={() => setActiveSource(activeSource === source ? null : source)}
                          >
                            {txText.source[source]}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </header>

        <div className="md:hidden">
          <div className="rounded-[12px] border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-full rounded-[8px] border border-[var(--border-color)] bg-[var(--bg-deep)] px-3 text-[13px] text-[var(--text-primary)] outline-none"
              placeholder={txText.search}
            />
          </div>
        </div>

        <div className="hidden md:block">
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder={txText.search}
            period={period}
            onPeriodChange={setPeriod}
            periodOptions={periodOptions}
          />
        </div>

        {period === "custom" ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-[8px] border border-[var(--border-color)] bg-[var(--bg-deep)] px-3 py-2 text-[12px] text-[var(--text-primary)]"
            />
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-[8px] border border-[var(--border-color)] bg-[var(--bg-deep)] px-3 py-2 text-[12px] text-[var(--text-primary)]"
            />
          </div>
        ) : null}

        <div className="hidden md:block">
          <FilterChips
            title={txText.filters}
            allLabel={txText.allCategories}
            seeAllLabel={txText.seeAll}
            categories={visibleCategories}
            activeCategoryId={activeCategoryId}
            onCategoryChange={setActiveCategoryId}
            sourceLabels={txText.source}
            activeSource={activeSource}
            onSourceChange={setActiveSource}
          />
        </div>

        {selectedIds.size > 0 ? (
          <BulkActionBar
            selectedCount={selectedIds.size}
            selectedLabel={txText.selected}
            changeCategoryLabel={txText.changeCategory}
            deleteLabel={txText.delete}
            closeLabel={txText.closeSelection}
            categoryPlaceholder={txText.selectCategory}
            categoryDialogTitle={txText.categoryDialogTitle}
            applyLabel={txText.apply}
            cancelLabel={txText.cancel}
            categories={categories.map((category) => ({ id: category.id, label: category.name }))}
            pendingCategoryId={bulkCategoryId}
            onPendingCategoryIdChange={setBulkCategoryId}
            onApplyCategory={applyBulkCategory}
            onDelete={handleBulkDelete}
            onClear={() => setSelectedIds(new Set())}
          />
        ) : null}

        {loading ? (
          <div className="rounded-[12px] border border-[var(--border-color)] bg-[var(--bg-secondary)] p-6 text-[var(--text-muted)]">
            {isHe ? "טוען..." : "Loading..."}
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="rounded-[12px] border border-[var(--border-color)] bg-[var(--bg-secondary)] p-6 text-[var(--text-muted)]">
            {txText.noTransactions}
          </div>
        ) : view === "grouped" ? (
          <div className="flex flex-col gap-[18px]">
            {groups.map((group) => (
              <DayGroup
                key={group.key}
                label={group.label}
                itemLabel={txText.items}
                currencySymbol={currency.symbol}
                locale={locale}
                mode="grouped"
                transactions={mappedRows(group.txs, "grouped")}
                selectedIds={selectedIds}
                showSourcePill
                onToggleSelect={toggleSelect}
                onEdit={handleEdit}
                onDelete={setDeleteTransactionId}
                editTooltip={rowActionLabels.editTooltip}
                editDisabledTooltip={rowActionLabels.editDisabledTooltip}
                deleteTooltip={rowActionLabels.deleteTooltip}
              />
            ))}
          </div>
        ) : (
          <DayGroup
            label=""
            itemLabel={txText.items}
            currencySymbol={currency.symbol}
            locale={locale}
            mode="list"
            transactions={mappedRows(filteredTransactions, "list")}
            selectedIds={selectedIds}
            showSourcePill
            onToggleSelect={toggleSelect}
            onEdit={handleEdit}
            onDelete={setDeleteTransactionId}
            editTooltip={rowActionLabels.editTooltip}
            editDisabledTooltip={rowActionLabels.editDisabledTooltip}
            deleteTooltip={rowActionLabels.deleteTooltip}
          />
        )}
      </div>

      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImportComplete={() =>
          void loadTransactions({
            from: format(dateRange.from, "yyyy-MM-dd"),
            to: format(dateRange.to, "yyyy-MM-dd"),
          })
        }
      />

      <EditTransactionDialog
        open={!!editingTransactionId}
        onOpenChange={(open) => {
          if (!open) setEditingTransactionId(null);
        }}
        transaction={editingTransaction}
        categories={categories}
        onSave={handleSaveEdit}
      />

      <AlertDialog
        open={!!deleteTransactionId}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteTransactionId(null);
        }}
      >
        <AlertDialogContent dir={isHe ? "rtl" : "ltr"} className="border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--text-primary)]">
              {t("transactions.deleteConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-secondary)]">
              {t("transactions.confirmDeleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isHe ? "flex-row-reverse gap-2" : ""}>
            <AlertDialogCancel
              disabled={isDeleting}
              className="border-[var(--border-color)] bg-transparent text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            >
              {t("transactions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmDelete();
              }}
              disabled={isDeleting}
              className="bg-[var(--expense)] text-white hover:bg-[var(--expense)]/90"
            >
              {isDeleting ? t("transactions.deleting") : t("transactions.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Button
        className="fixed bottom-5 z-40 h-12 rounded-[999px] bg-[var(--fg-accent)] px-5 text-[13px] font-medium text-white shadow-[0_8px_20px_rgba(97,97,255,0.35)] hover:bg-[var(--fg-accent-hover)] md:hidden"
        style={isHe ? { left: 16 } : { right: 16 }}
        onClick={() => navigate("/add-expense")}
      >
        <Plus size={14} className="me-2" />
        {txText.add}
      </Button>
    </div>
  );
}
